import { useEffect, type ReactNode } from 'react'
import { LeadLogo } from './LeadLogo'

/**
 * Right-hand slide-over holding the whole lead detail.
 *
 * A drawer rather than a second column: the table is the primary surface now,
 * and a permanently-split view leaves neither side enough room once six columns
 * are on. Clicking the table behind it closes it, so getting back to the list is
 * a click anywhere rather than a hunt for the ✕.
 *
 * ## It names the lead, as of 4 Sep 2026
 *
 * The title was `aria-label` only and nothing on screen said which company was
 * open — `LeadIdentity` did it, along with the logo, which is why the inline
 * layout showed both twice. Now the logo and the name live here, once, beside
 * the close button. Inline the table row does the same job, so `LeadIdentity`
 * does it in neither.
 */
export function LeadDrawer({
  title,
  logoUrl,
  onClose,
  children,
}: {
  title: string
  /** The company's logo, so the drawer header is the identity strip it replaced. */
  logoUrl?: string
  onClose: () => void
  children: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <aside
      className="drawer"
      role="dialog"
      aria-label={title}
      onClick={(e) => e.stopPropagation()}
    >
      <header className="drawer__head">
        <LeadLogo name={title} src={logoUrl ?? ''} size={34} radius={9} />
        <h2 className="drawer__title">{title}</h2>
        <button className="drawer__closebtn" onClick={onClose} aria-label="Close lead">
          ✕
        </button>
      </header>
      {children}
    </aside>
  )
}
